import React from "react"
import Reveal from "../components/Reveal.jsx"
import Testimonials from "../components/Testimonials.jsx"
import Pricing from "../components/Pricing.jsx"
import Faq from "../components/Faq.jsx"

// Shopify-merchant landing (2026-07-17, /shopify). The destination for
// App Store visitors, Product Hunt traffic, and Shopify-targeted posts.
// Own § 01–04 sections + reused Pricing (§ 05) / Faq (§ 06) so the
// hardcoded numbering in the shared components stays consistent.
// Testimonials is unnumbered by design (conditional section).

const INSTALL_STEPS = [
  {
    title: "Install from the App Store.",
    body:
      "One click. ShopShift asks for ZERO admin permissions — it can't read your orders, customers, or settings. The app connects through a theme embed, nothing else.",
  },
  {
    title: "Toggle the embed. Done.",
    body:
      "The theme editor opens with the ShopShift tracker pre-selected — flip it on and save. No code, no api keys, no snippet copy-paste, under two minutes.",
  },
  {
    title: "The AI takes it from there.",
    body:
      "It renders your store like a shopper, learns your products and voice, and starts proposing A/B tests. You approve from inside Shopify admin — or let it run.",
  },
]

const TRUST_CARDS = [
  {
    eyebrow: "Your language",
    title: "Variants in your store's own language.",
    body:
      "The AI reads the language your store is written in and writes every headline, button, and offer in it — Danish store, Danish A/B copy. No translation pass, no English-only assumption.",
  },
  {
    eyebrow: "Your rules",
    title: "You decide what the AI may touch.",
    body:
      "Text only, text + styles, or full rein — plus per-element overrides and hard-protected selectors. Checkout is never touched. Every change is verified in a real browser before a single shopper sees it.",
  },
  {
    eyebrow: "Your billing",
    title: "Billed through Shopify.",
    body:
      "Install from the App Store and the subscription runs on your Shopify invoice — approve it inside admin like any other app. 30-day free trial, cancel any time.",
  },
]

function Hero({ ctaHref, ctaLabel }) {
  return (
    <section className="max-w-6xl mx-auto px-6 pt-24 pb-28">
      <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ai)] mb-8">
        § 01 · Built for Shopify
      </p>
      <h1 className="font-sans font-medium text-[color:var(--color-ink-primary)] text-[40px] md:text-[52px] tracking-[-0.035em] leading-[1.05] max-w-3xl">
        AI that runs A/B tests on your Shopify store — by itself.
      </h1>
      <p className="mt-6 font-sans text-[18px] tracking-[-0.01em] text-[color:var(--color-ink-secondary)] max-w-2xl leading-[1.5]">
        Install the app, toggle one theme embed, and ShopShift finds what to test, writes on-brand
        variants in your store&rsquo;s own language, verifies them in a real browser, and ships the
        winners. You stay in control of every change.
      </p>

      <div className="mt-12 flex items-center gap-4 flex-wrap">
        <a
          href={ctaHref}
          className="inline-flex items-center bg-[color:var(--color-brand)] text-white font-sans text-[14px] font-medium px-5 py-3 rounded-[var(--radius-button)] hover:opacity-90 transition-opacity"
        >
          {ctaLabel}
        </a>
        <a
          href="#how-it-works"
          className="font-sans text-[14px] font-medium text-[color:var(--color-ai)] hover:underline"
        >
          How it works →
        </a>
      </div>
      <p className="mt-4 font-mono text-[11px] tracking-[0.04em] text-[color:var(--color-ink-faint)]">
        30-day free trial · billed through Shopify · zero admin permissions
      </p>
    </section>
  )
}

function InstallSteps() {
  return (
    <section
      id="how-it-works"
      className="border-t border-[color:var(--color-rule)] bg-[color:var(--color-surface-page)]"
    >
      <div className="max-w-6xl mx-auto px-6 py-24">
        <Reveal>
          <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ai)] mb-4">
            § 02 · From install to first test
          </p>
          <h2 className="font-sans text-[40px] tracking-[-0.035em] font-medium leading-[1.1] max-w-2xl">
            Two minutes of setup. Then it never stops.
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-[color:var(--color-rule)] border border-[color:var(--color-rule)] rounded-[var(--radius-card-lg)] overflow-hidden">
          {INSTALL_STEPS.map((step, idx) => (
            <Reveal key={step.title} delay={idx * 120}>
              <div className="bg-[color:var(--color-surface-card)] p-10 h-full flex flex-col gap-5">
                <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ink-tertiary)]">
                  {String(idx + 1).padStart(2, "0")}
                </p>
                <h3 className="font-sans text-[22px] tracking-[-0.025em] font-medium leading-[1.2] text-[color:var(--color-ink-primary)]">
                  {step.title}
                </h3>
                <p className="font-sans text-[14px] text-[color:var(--color-ink-secondary)] leading-[1.6]">
                  {step.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

function TrustAndLanguage() {
  return (
    <section className="border-t border-[color:var(--color-rule)] bg-[color:var(--color-surface-card)]">
      <div className="max-w-6xl mx-auto px-6 py-24">
        <Reveal>
          <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ai)] mb-4">
            § 03 · Why merchants trust it
          </p>
          <h2 className="font-sans text-[40px] tracking-[-0.035em] font-medium leading-[1.1] max-w-2xl">
            Any language. Your rules. Shopify billing.
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-[color:var(--color-rule)] border border-[color:var(--color-rule)] rounded-[var(--radius-card-lg)] overflow-hidden">
          {TRUST_CARDS.map((card, idx) => (
            <Reveal key={card.eyebrow} delay={idx * 120}>
              <div className="bg-[color:var(--color-surface-page)] p-10 h-full flex flex-col gap-5">
                <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ink-tertiary)]">
                  {card.eyebrow}
                </p>
                <h3 className="font-sans text-[22px] tracking-[-0.025em] font-medium leading-[1.2] text-[color:var(--color-ink-primary)]">
                  {card.title}
                </h3>
                <p className="font-sans text-[14px] text-[color:var(--color-ink-secondary)] leading-[1.6]">
                  {card.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

function DashboardTeaser() {
  return (
    <section className="border-t border-[color:var(--color-rule)] bg-[color:var(--color-surface-page)]">
      <div className="max-w-6xl mx-auto px-6 py-24">
        <Reveal>
          <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ai)] mb-4">
            § 04 · Inside Shopify admin
          </p>
          <h2 className="font-sans text-[40px] tracking-[-0.035em] font-medium leading-[1.1] max-w-2xl">
            Review, approve, and watch results without leaving admin.
          </h2>
          <p className="mt-6 font-sans text-[16px] text-[color:var(--color-ink-secondary)] max-w-2xl leading-[1.6]">
            ShopShift lives in your Shopify sidebar: every proposal comes with a before/after
            preview and plain-English reasoning, every experiment shows its live numbers, and
            winners are declared with Bayesian math — not gut feeling. The full dashboard on
            shopshift.io is one click away when you want to go deeper.
          </p>
        </Reveal>
      </div>
    </section>
  )
}

export default function ShopifyView({ priceCents = 9900, appStoreUrl = null, translations }) {
  const ctaHref = appStoreUrl || "/users/sign_up"
  const ctaLabel = appStoreUrl ? translations.install_cta : translations.signup_cta

  return (
    <>
      <Hero ctaHref={ctaHref} ctaLabel={ctaLabel} />
      <InstallSteps />
      <TrustAndLanguage />
      <DashboardTeaser />
      <Testimonials />
      <Pricing priceCents={priceCents} copy={{ ...translations.pricing, cta: ctaLabel }} ctaHref={ctaHref} />
      <Faq entries={translations.faq} />
    </>
  )
}
